function setHidden(el, isHidden) {
  if (isHidden) {
    el.setAttribute('hidden', '');
  } else {
    el.removeAttribute('hidden');
  }
}

// Background media: static (no parallax). The video's sources ship as
// data-src and are attached only on screens ≥ 750px: phones show the poster
// image and never download the video. Plays only while the hero is on
// screen, and never autoplays with reduced motion.
if (!customElements.get('hero-cinematic-media')) {
  customElements.define(
    'hero-cinematic-media',
    class HeroCinematicMedia extends HTMLElement {
      constructor() {
        super();
        this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.onIntersect = this.onIntersect.bind(this);
        this.onDesktopChange = this.onDesktopChange.bind(this);
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.isVisible = false;
        if (this.desktopQuery.matches) {
          this.attachSources();
        } else {
          // A phone turned to landscape or a window widened past 750px.
          this.desktopQuery.addEventListener('change', this.onDesktopChange);
        }
        this.visibilityObserver = new IntersectionObserver(this.onIntersect);
        this.visibilityObserver.observe(this);
      }

      disconnectedCallback() {
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        this.desktopQuery.removeEventListener('change', this.onDesktopChange);
      }

      attachSources() {
        if (this.sourcesAttached) return;
        this.sourcesAttached = true;
        this.video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        this.video.preload = 'auto';
        this.video.load();
      }

      onDesktopChange(event) {
        if (!event.matches) return;
        this.desktopQuery.removeEventListener('change', this.onDesktopChange);
        this.attachSources();
        if (this.isVisible) this.onIntersect([{ isIntersecting: true }]);
      }

      onIntersect(entries) {
        const entry = entries[entries.length - 1];
        this.isVisible = entry.isIntersecting;
        if (!this.sourcesAttached) return;

        if (entry.isIntersecting) {
          // Reduced motion: no autoplay; it plays only if started by hand.
          const autoplayAllowed = !this.reduceMotionQuery.matches || this.video.dataset.userPaused === 'false';
          if (this.video.dataset.userPaused !== 'true' && autoplayAllowed) {
            this.video.play();
          } else if (!autoplayAllowed) {
            this.video.pause();
          }
        } else {
          this.video.pause();
        }
      }
    }
  );
}

if (!customElements.get('hero-cinematic-media-toggle')) {
  customElements.define(
    'hero-cinematic-media-toggle',
    class HeroCinematicMediaToggle extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.pauseIcon = this.querySelector('[data-icon="pause"]');
        this.playIcon = this.querySelector('[data-icon="play"]');
        this.pauseLabel = this.querySelector('[data-label="pause"]');
        this.playLabel = this.querySelector('[data-label="play"]');
        this.video = this.button
          ? document.getElementById(this.button.getAttribute('aria-controls'))
          : null;
        this.onClick = this.onClick.bind(this);
        this.onVideoPlay = this.onVideoPlay.bind(this);
        this.onVideoPause = this.onVideoPause.bind(this);

        if (this.button) {
          this.button.addEventListener('click', this.onClick);
        }

        if (this.video) {
          this.video.addEventListener('play', this.onVideoPlay);
          this.video.addEventListener('pause', this.onVideoPause);
        }
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this.onClick);
        }

        if (this.video) {
          this.video.removeEventListener('play', this.onVideoPlay);
          this.video.removeEventListener('pause', this.onVideoPause);
        }
      }

      onClick() {
        if (!this.video) return;

        if (this.video.paused) {
          this.video.dataset.userPaused = 'false';
          this.video.play();
        } else {
          this.video.dataset.userPaused = 'true';
          this.video.pause();
        }
      }

      onVideoPlay() {
        setHidden(this.pauseIcon, false);
        setHidden(this.playIcon, true);
        setHidden(this.pauseLabel, false);
        setHidden(this.playLabel, true);
      }

      onVideoPause() {
        setHidden(this.pauseIcon, true);
        setHidden(this.playIcon, false);
        setHidden(this.pauseLabel, true);
        setHidden(this.playLabel, false);
      }
    }
  );
}
